import { useEffect, useState } from 'react';
import liff from '@line/liff';
import { DateCalendar, formatDateJa, type BookingDay } from './DateCalendar';
import firstVisitCharm from './assets/first-visit-charm.webp';

type Form = {
  name: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  female: number;
  male: number;
  idCard: boolean;
  confirmAge: boolean;
  confirmPhoto: boolean;
};

// 予約受付時間: 20:00〜22:00 (15分単位)
const TIME_SLOTS = [
  '20:00', '20:15', '20:30', '20:45',
  '21:00', '21:15', '21:30', '21:45',
  '22:00',
];
const COUNT_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8];

// 予約可能日（翌日から数か月分の初回受付の可否）。正は Club Agent のカレンダーで、
// /api/availability（Pages Functions）経由で読む。曜日や月末の決まった規則はフォームに持たない
type Availability =
  | { state: 'loading' }
  | { state: 'ready'; days: BookingDay[] }
  | { state: 'error' };

export function App() {
  const [status, setStatus] =
    useState<'loading' | 'ready' | 'submitting' | 'done' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<Availability>({ state: 'loading' });
  const [form, setForm] = useState<Form>({
    name: '',
    date: '',
    time: '',
    female: 0,
    male: 0,
    idCard: false,
    confirmAge: false,
    confirmPhoto: false,
  });

  const loadAvailability = () => {
    setAvailability({ state: 'loading' });
    fetch('/api/availability')
      .then(res => (res.ok
        ? (res.json() as Promise<{ days: BookingDay[] }>)
        : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(body => setAvailability({ state: 'ready', days: body.days }))
      .catch(() => setAvailability({ state: 'error' }));
  };
  useEffect(loadAvailability, []);

  useEffect(() => {
    if (import.meta.env.DEV) {
      setStatus('ready');
      return;
    }
    const liffId = import.meta.env.VITE_LIFF_ID;
    if (!liffId) {
      setStatus('error');
      setError('VITE_LIFF_ID が設定されていません');
      return;
    }
    liff
      .init({ liffId })
      .then(async () => {
        if (!liff.isLoggedIn()) {
          liff.login();
          return;
        }
        setStatus('ready');
      })
      .catch(err => {
        setStatus('error');
        setError(`LIFF init failed: ${err.message}`);
      });
  }, []);

  const update = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  // カレンダーで選べるのは受付可能な日だけ。実際の受付可否は送信時にサーバでも再チェックする
  const isOpenDate = (date: string): boolean =>
    availability.state === 'ready' && availability.days.some(d => d.date === date && d.status === 'open');

  const validate = (): string | null => {
    if (!form.name.trim()) return 'お名前をご入力ください。';
    if (!form.date) return 'ご希望日をご選択ください。';
    if (!isOpenDate(form.date)) return 'この日は初回のご予約を承っておりません。別の日付をお選びください。';
    if (!form.time) return 'ご希望時刻をご選択ください。';
    if (form.female + form.male <= 0) return '人数を1名以上ご入力ください。';
    if (!form.idCard) return '身分証持参の確認にチェックをお願いします。';
    if (!form.confirmAge) return '年齢確認にチェックをお願いします。';
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setStatus('submitting');
    setError(null);
    try {
      const idToken = liff.getIDToken();
      if (!idToken) throw new Error('ID トークンを取得できませんでした');

      const res = await fetch('/api/reserve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          date: form.date,
          time: form.time,
          female: form.female,
          male: form.male,
          confirms: {
            age: form.confirmAge,
            photo: form.confirmPhoto,
          },
          idToken,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        // 開いてから送信までの間に受付不可になった日は、カレンダーを読み直して選び直してもらう
        if (res.status === 409) {
          update('date', '');
          loadAvailability();
        }
        throw new Error(text || `HTTP ${res.status}`);
      }
      setStatus('done');
      setTimeout(() => liff.closeWindow(), 1500);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (status === 'loading') {
    return <main className="container"><p>読み込み中...</p></main>;
  }
  if (status === 'done') {
    return (
      <main className="container">
        <h1>ご予約を承りました</h1>
        <p>このウィンドウは自動で閉じます。</p>
      </main>
    );
  }

  return (
    <main className="container">
      {/* 冒頭は特典バナー・プラン・注意書きをひとまとまりにして、フォームとは下線で区切る */}
      <header className="intro">
        <img
          className="intro-hero"
          src={firstVisitCharm}
          width={1672}
          height={941}
          alt="LINE予約限定 初回来店特典：Lillionオリジナル アクリルめじるしチャームをプレゼント。60分 3,000円 缶もの飲み放題"
        />
        {/* 見出しは特典バナーが兼ねるので画面には出さず、読み上げ用にだけ残す */}
        <h1 className="visually-hidden">Lillion 初回限定予約フォーム</h1>
        <div className="intro-plan">
          <div className="plan-label">LINE予約 特別プラン</div>
          <div className="plan-price">¥3,000 <span>／60分</span></div>
          <div className="plan-desc">焼酎・割り物・缶物 飲み放題</div>
        </div>
        <ul className="intro-notes">
          <li>当日の予約をご希望のお客様は、お電話にてお問い合わせください（TEL：<a href="tel:0362789125">03-6278-9125</a>）。</li>
          <li>男性のお客様は「LINE予約 特別プラン」対象外となり、通常料金でのご案内となります。</li>
        </ul>
      </header>

      <form onSubmit={submit} className="form" noValidate>
        <label className="field">
          <span>お名前（予約名）<em>*</em></span>
          <input
            type="text"
            value={form.name}
            onChange={e => update('name', e.target.value)}
            placeholder="山田 太郎"
            autoComplete="name"
          />
        </label>

        <div className="field">
          <span>ご希望日<em>*</em></span>
          {availability.state === 'loading' && <p className="cal-status">予約可能日を読み込み中...</p>}
          {availability.state === 'error' && (
            <p className="cal-status field-error">
              予約可能日を読み込めませんでした。お手数ですがお電話（
              <a href="tel:0362789125">03-6278-9125</a>
              ）にてお問い合わせください。
              <button type="button" className="cal-retry" onClick={loadAvailability}>再読み込み</button>
            </p>
          )}
          {availability.state === 'ready' && (
            <DateCalendar days={availability.days} value={form.date} onChange={d => update('date', d)} />
          )}
          {form.date && <small className="cal-picked">選択中：{formatDateJa(form.date)}</small>}
        </div>

        <label className="field">
          <span>ご希望時刻<em>*</em></span>
          <select
            value={form.time}
            onChange={e => update('time', e.target.value)}
          >
            <option value="">選択してください</option>
            {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <small>入店可能時間：20:00〜22:00</small>
        </label>

        <fieldset className="field-group">
          <legend>人数<em>*</em></legend>
          <div className="row">
            <label className="field">
              <span>女性</span>
              <select
                value={form.female}
                onChange={e => update('female', Number(e.target.value))}
              >
                {COUNT_OPTIONS.map(n => <option key={n} value={n}>{n}名</option>)}
              </select>
            </label>
            <label className="field">
              <span>男性</span>
              <select
                value={form.male}
                onChange={e => update('male', Number(e.target.value))}
              >
                {COUNT_OPTIONS.map(n => <option key={n} value={n}>{n}名</option>)}
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset className="field-group">
          <legend>ご確認事項<em>*</em></legend>
          <label className="check">
            <input
                type="checkbox"
                checked={form.idCard}
                onChange={e => update('idCard', e.target.checked)}
            />
            <span>ご来店の際は顔写真付き身分証（マイナンバーカードか運転免許証）をご持参ください。</span>
          </label>
          <small className="check-note">
            ※ 受付時に、身分証のご提示をお願いしております。<br/>
            ※ 有効期限内の原本に限ります。<br/>
          </small>
          <label className="check">
            <input
                type="checkbox"
                checked={form.confirmAge}
                onChange={e => update('confirmAge', e.target.checked)}
            />
            <span>満20歳以上であることに相違ありません。</span>
          </label>
          <label className="check">
            <input
                type="checkbox"
                checked={form.confirmPhoto}
                onChange={e => update('confirmPhoto', e.target.checked)}
            />
            <span>店内での広報目的の撮影に同意します。<span className="optional">（任意）</span></span>
          </label>
          <small className="check-note">
            ※ 店内にて広報・SNS掲載等を目的とした撮影を行う場合がございます。<br/>
            ※ お客様が映り込んだ際は、個人が特定されないよう、当店にてモザイク等の処理を行います。<br/>
          </small>
        </fieldset>

        {error && <p className="error">{error}</p>}

        <button type="submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? '送信中...' : '予約を送信する'}
        </button>
      </form>
    </main>
  );
}
